import { dentalPrices, surgeryPrices, hairTransplantPrices } from '@/mocks/pricingLists';
import { getStore } from './store';

// ── Types ────────────────────────────────────────────

interface AdminTreatment {
  id: string;
  category: 'hair' | 'dental' | 'surgery';
  name: string;
  description: string;
  priceAL: string;
  priceDE: string;
  priceAT: string;
  priceCH: string;
  savingsPercent: number;
  stay?: string;
}

export interface PricingTreatment {
  name: string;
  price: string;
  stay?: string;
}

export interface SavingsComparison {
  procedure: string;
  dePrice: string;
  alPrice: string;
  savings: string;
}

// ── Stay lookup from pricingLists ────────────────────

const STAY_MAP: Record<string, string> = {};

(function buildStayMap() {
  const surgeryDe = (surgeryPrices as Record<string, PricingTreatment[]>).de || [];
  surgeryDe.forEach((item) => {
    if (item.stay) STAY_MAP[item.name] = item.stay;
  });
  const hairDe = (hairTransplantPrices as Record<string, PricingTreatment[]>).de || [];
  hairDe.forEach((item) => {
    if (item.stay) STAY_MAP[item.name] = item.stay;
  });
})();

// ── Translation lookup by German name ────────────────

const TRANSLATION_MAP: Record<string, Record<string, string>> = {};

(function buildTranslationMap() {
  const langs = ['de', 'en', 'fr', 'it', 'sq'];
  const deDental = (dentalPrices as Record<string, PricingTreatment[]>).de || [];
  const deSurgery = (surgeryPrices as Record<string, PricingTreatment[]>).de || [];
  const deHair = (hairTransplantPrices as Record<string, PricingTreatment[]>).de || [];

  langs.forEach((lang) => {
    const dentalLang = (dentalPrices as Record<string, PricingTreatment[]>)[lang] || [];
    const surgeryLang = (surgeryPrices as Record<string, PricingTreatment[]>)[lang] || [];
    const hairLang = (hairTransplantPrices as Record<string, PricingTreatment[]>)[lang] || [];

    deDental.forEach((item, i) => {
      if (!TRANSLATION_MAP[item.name]) TRANSLATION_MAP[item.name] = {};
      TRANSLATION_MAP[item.name][lang] = dentalLang[i]?.name || item.name;
    });
    deSurgery.forEach((item, i) => {
      if (!TRANSLATION_MAP[item.name]) TRANSLATION_MAP[item.name] = {};
      TRANSLATION_MAP[item.name][lang] = surgeryLang[i]?.name || item.name;
    });
    deHair.forEach((item, i) => {
      if (!TRANSLATION_MAP[item.name]) TRANSLATION_MAP[item.name] = {};
      TRANSLATION_MAP[item.name][lang] = hairLang[i]?.name || item.name;
    });
  });
})();

function translateName(germanName: string, lang: string): string {
  return TRANSLATION_MAP[germanName]?.[lang] || germanName;
}

export function getTranslatedName(germanName: string, lang: string): string {
  return translateName(germanName, lang);
}

// ── Core: read admin treatments from localStorage ────

function getAdminTreatments(): AdminTreatment[] {
  // Live treatments from the Supabase-hydrated store, mapped to the local shape.
  return getStore().treatments.map((t) => ({
    id: t.id,
    category: t.category,
    name: t.name,
    description: t.description ?? '',
    priceAL: t.price_al ?? '',
    priceDE: t.price_de ?? '',
    priceAT: t.price_at ?? '',
    priceCH: t.price_ch ?? '',
    savingsPercent: t.savings_percent ?? 0,
    stay: t.stay ?? undefined,
  }));
}

function hasAdminTreatments(): boolean {
  return getAdminTreatments().length > 0;
}

// ── Convert to pricing component format ──────────────

export function getPricingTreatments(
  category: 'dental' | 'surgery' | 'hair',
  lang: string,
): PricingTreatment[] {
  const adminTreatments = getAdminTreatments();

  if (adminTreatments.length > 0) {
    const filtered = adminTreatments.filter((t) => t.category === category);
    if (filtered.length > 0) {
      return filtered.map((t) => ({
        name: translateName(t.name, lang),
        price: t.priceAL,
        stay: t.stay || STAY_MAP[t.name],
      }));
    }
  }

  // Fallback: pricingLists
  const fallbackMap: Record<string, Record<string, PricingTreatment[]>> = {
    dental: dentalPrices as Record<string, PricingTreatment[]>,
    surgery: surgeryPrices as Record<string, PricingTreatment[]>,
    hair: hairTransplantPrices as Record<string, PricingTreatment[]>,
  };
  return fallbackMap[category]?.[lang] || fallbackMap[category]?.en || [];
}

export function hasCustomPricingData(): boolean {
  return hasAdminTreatments();
}

// ── Savings comparison (from admin data) ─────────────

export function getSavingsComparisons(): SavingsComparison[] {
  const adminTreatments = getAdminTreatments();

  if (adminTreatments.length > 0) {
    // Pick top 6 by savings percent, one per "area"
    const picked: AdminTreatment[] = [];
    const seen = new Set<string>();

    const priorityNames = [
      'Zahnimplantat',
      'Nasenkorrektur',
      'Brustvergrößerung (Mentor)',
      'Facelifting',
      'FUE Haartransplantation (bis 5.000 Grafts)',
      'Liposuktion (Total Body)',
    ];

    priorityNames.forEach((name) => {
      const found = adminTreatments.find((t) => t.name === name);
      if (found && !seen.has(found.name)) {
        picked.push(found);
        seen.add(found.name);
      }
    });

    // Fill remaining slots
    const sorted = [...adminTreatments].sort((a, b) => b.savingsPercent - a.savingsPercent);
    for (const t of sorted) {
      if (picked.length >= 6) break;
      if (!seen.has(t.name)) {
        picked.push(t);
        seen.add(t.name);
      }
    }

    return picked.map((t) => ({
      procedure: t.name,
      dePrice: `${t.priceDE} €`,
      alPrice: `${t.priceAL} €`,
      savings: `${t.savingsPercent}%`,
    }));
  }

  // Fallback
  return [
    { procedure: 'Zahnimplantat (pro Stück)', dePrice: '1.200–1.800 €', alPrice: '350–400 €', savings: '70–78%' },
    { procedure: 'Nasenkorrektur', dePrice: '6.000–8.000 €', alPrice: '2.200 €', savings: '63–73%' },
    { procedure: 'Brustvergrößerung', dePrice: '8.000–10.000 €', alPrice: '4.200 €', savings: '48–58%' },
    { procedure: 'Facelifting', dePrice: '12.000–15.000 €', alPrice: '4.200 €', savings: '65–72%' },
    { procedure: 'Haartransplantation (5.000 Grafts)', dePrice: '6.000–10.000 €', alPrice: '1.500–2.000 €', savings: '75–80%' },
    { procedure: 'Liposuktion (Total Body)', dePrice: '10.000–15.000 €', alPrice: '5.000 €', savings: '50–67%' },
  ];
}

// ── Schema builders (for SEO) ────────────────────────

function parseGermanNumber(raw: string): number {
  return Number(raw.replace(/\./g, '').replace(',', '.'));
}

function buildServiceOffer(name: string, priceRaw: string, position: number, currency: string = 'EUR') {
  const clean = priceRaw.replace(/\s*–\s*/g, '-').replace(/\s+/g, '');
  const rangeMatch = clean.match(/^([\d.]+)-([\d.]+)$/);
  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    eligibleRegion: { '@type': 'Country', name: 'AL' },
  };
  if (rangeMatch) {
    offer.minPrice = parseGermanNumber(rangeMatch[1]);
    offer.maxPrice = parseGermanNumber(rangeMatch[2]);
  } else {
    offer.price = parseGermanNumber(clean);
  }
  return {
    '@type': 'ListItem',
    position,
    item: {
      '@type': 'MedicalProcedure',
      name,
      howPerformed: 'https://schema.org/HowPerformed',
      offers: offer,
    },
  };
}

export function buildDentalSchema() {
  const items = getPricingTreatments('dental', 'de');
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Dental Price List – Esteva Clinic Tirana',
    description: 'Transparente Preisliste für Zahnbehandlungen in Tirana, Albanien. Zahnimplantate, Kronen, Veneers und mehr.',
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => buildServiceOffer(item.name, item.price, i + 1)),
  };
}

export function buildSurgerySchema() {
  const items = getPricingTreatments('surgery', 'de');
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Plastic Surgery Price List – Esteva Clinic Tirana',
    description: 'Transparente Preisliste für plastische und ästhetische Chirurgie in Tirana. Rhinoplastik, Brustvergrößerung, Liposuktion & mehr.',
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => buildServiceOffer(item.name, item.price, i + 1)),
  };
}

export function buildHairSchema() {
  const items = getPricingTreatments('hair', 'de');
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Hair Transplant Price List – Esteva Clinic Tirana',
    description: 'Transparente Preisliste für FUE Haartransplantation in Tirana. Bis 5.000 Grafts mit modernster Technik.',
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => buildServiceOffer(item.name, item.price, i + 1)),
  };
}